import React, { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import {
  type IRaangeLabelProps,
  RaangeLabel,
} from "src/shared/components/RaangeLabel";
import { Formatter } from "src/shared/formatters/Formatter";
import { StoreName } from "src/stores/formatters/StoreFormatters";
import type {
  Subscriber,
  SubscriberDetail,
} from "src/subscribers/objects/Subscriber";

export interface ISubscriberLabelProps extends Omit<IRaangeLabelProps, "fa"> {}

export const SubscriberLabel = (props: ISubscriberLabelProps) => {
  return (
    <RaangeLabel fa="building" {...props}>
      <FormattedMessage id="subscribers" />
    </RaangeLabel>
  );
};

export const SubscribersEditLabel = (props: ISubscriberLabelProps) => {
  return (
    <RaangeLabel fa="plus" {...props}>
      <FormattedMessage id="subscribers.edit" />
    </RaangeLabel>
  );
};

export interface ISubscriberFormatterProps<T extends Subscriber = Subscriber>
  extends Omit<IRaangeLabelProps, "fa"> {
  children: T;
}

export const SubscriberName = ({ children }: ISubscriberFormatterProps) => {
  return (
    <>
      {children.firstName} {children.lastName}
    </>
  );
};

export const SubscriberPlatform = ({
  children,
}: {
  children: "SMS" | "EMAIL";
}) => {
  if (children === "SMS") {
    return <FormattedMessage id="subscribers.sms" />;
  } else {
    return <FormattedMessage id="subscribers.email" />;
  }
};

export const SubscriberStore = ({
  children: subscriber,
}: ISubscriberFormatterProps<SubscriberDetail>) => {
  return (
    <Formatter value={(subscriber.storeName || subscriber.storeNumber) ?? ""}>
      <StoreName hasLink showsStoreNumber>
        {subscriber}
      </StoreName>
    </Formatter>
  );
};

export const SubscriberDigitalStore = ({
  children: {
    digitalStoreUUID,
    digitalStoreName,
    digitalStoreNumber,
    merchantUUID,
  },
}: ISubscriberFormatterProps<SubscriberDetail>) => {
  const digitalStore = useMemo(
    () => ({
      storeUUID: digitalStoreUUID,
      storeName: digitalStoreName,
      storeNumber: digitalStoreNumber,
      merchantUUID,
    }),
    [digitalStoreName, digitalStoreNumber, digitalStoreUUID, merchantUUID],
  );
  return (
    <Formatter
      value={(digitalStore.storeName || digitalStore.storeNumber) ?? ""}
    >
      <StoreName hasLink showsStoreNumber>
        {digitalStore}
      </StoreName>
    </Formatter>
  );
};

export const SubscriberMultiStore = ({
  children: { multiStoreUUID, multiStoreName, multiStoreNumber, merchantUUID },
}: ISubscriberFormatterProps<SubscriberDetail>) => {
  const multiStore = useMemo(
    () => ({
      storeUUID: multiStoreUUID,
      storeName: multiStoreName,
      storeNumber: multiStoreNumber,
      merchantUUID,
    }),
    [merchantUUID, multiStoreName, multiStoreNumber, multiStoreUUID],
  );
  return (
    <Formatter value={(multiStore.storeName || multiStore.storeNumber) ?? ""}>
      <StoreName hasLink showsStoreNumber>
        {multiStore}
      </StoreName>
    </Formatter>
  );
};
